import { useState, useCallback } from 'react';
import type { PillarId } from '../components/layout/navConfig';

const STORAGE_KEY = 'defaultPillar';

/** Valid pillar IDs that can be set as default (excludes admin). */
const VALID_DEFAULTS: PillarId[] = ['cultivate', 'connect', 'command', 'chronicle'];

function readStored(): PillarId | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw && VALID_DEFAULTS.includes(raw as PillarId)) return raw as PillarId;
  } catch { /* SSR / private browsing */ }
  return null;
}

/**
 * Read and write the user's preferred default pillar.
 * Stored in localStorage — instant, offline-safe, device-specific.
 */
export function useDefaultPillar() {
  const [defaultPillar, setDefaultPillarState] = useState<PillarId | null>(readStored);

  const setDefaultPillar = useCallback((id: PillarId | null) => {
    try {
      if (id && VALID_DEFAULTS.includes(id)) {
        localStorage.setItem(STORAGE_KEY, id);
      } else {
        localStorage.removeItem(STORAGE_KEY);
      }
    } catch { /* private browsing */ }
    setDefaultPillarState(id && VALID_DEFAULTS.includes(id) ? id : null);
  }, []);

  return { defaultPillar, setDefaultPillar } as const;
}
